import type { WidgetLabelSpec } from "../types/manifest";

/** Keeps the literal keys of a label list so `resolveLabels` can type its result. */
export function defineLabels<const T extends readonly WidgetLabelSpec[]>(specs: T): T {
  return specs;
}

/**
 * The texts to display: each label's instance override from `config.labels`,
 * or its default when the override is missing or blank.
 */
export function resolveLabels<const T extends readonly WidgetLabelSpec[]>(
  specs: T,
  config: unknown,
): Record<T[number]["key"], string> {
  const overrides =
    config && typeof config === "object" && "labels" in config
      ? (config as { labels?: unknown }).labels
      : undefined;
  const source =
    overrides && typeof overrides === "object" ? (overrides as Record<string, unknown>) : {};
  const result: Record<string, string> = {};
  for (const spec of specs) {
    const value = source[spec.key];
    result[spec.key] = typeof value === "string" && value.trim() ? value.trim() : spec.default;
  }
  return result as Record<T[number]["key"], string>;
}

const ADVANCED_KEY = /^(error|empty|noMatch|noEvent|notFound|noChannels|loading|dataUnavailable|metricUnavailable|notAvailable|noSample|retry|reset|zoomHint|clearFilters|loadMore|demoData|estimated|scopeRequired)/;

/** Whether the editor should tuck this label under "More texts". */
export function isAdvancedLabel(spec: WidgetLabelSpec): boolean {
  return spec.advanced ?? ADVANCED_KEY.test(spec.key);
}
